import {
  collection, doc, getDoc, setDoc, getDocs, addDoc, updateDoc, deleteDoc,
  query, orderBy, where, serverTimestamp, limit, onSnapshot,
  type Unsubscribe,
} from 'firebase/firestore';
import { db } from './firebase';

const SETTINGS_DOC = doc(db, 'settings', 'notifications');
const CUSTOM_COL = collection(db, 'custom_notifications');

interface NotificationRule {
  enabled: boolean;
  threshold?: number;
  days?: number;
  action: string;
  message: string;
  icon: string;
}

export interface NotificationSettings {
  tamkeen: Record<string, NotificationRule>;
  welcome: NotificationRule;
}

export interface CustomNotification {
  id: string;
  active?: boolean;
  createdAt?: unknown;
  [key: string]: unknown;
}

const DEFAULT_SETTINGS: NotificationSettings = {
  tamkeen: {
    masteryDrop: {
      enabled: true,
      threshold: 75,
      action: 'page',
      message: 'مستواك ممتاز بس يحتاج تثبيت! جلسة سريعة تفرق معك',
      icon: '💪',
    },
    inactivity: {
      enabled: true,
      days: 3,
      action: 'page',
      message: 'اشتقنا لإبداعك! تعال نكمّل المشوار مع بعض',
      icon: '🚀',
    },
    decliningCards: {
      enabled: true,
      threshold: 10,
      action: 'page',
      message: 'عندك مفاهيم قربت تنسى! دقائق مراجعة تنقذها',
      icon: '⚡',
    },
    masteryAchievement: {
      enabled: false,
      threshold: 90,
      action: 'silent',
      message: 'ماشاء الله عليك! مستواك عالي جداً، كمّل وما توقف',
      icon: '🏆',
    },
  },
  welcome: {
    enabled: true,
    message: 'يا هلا فيك! رحلتك مع موقع بذكاء تبدأ الآن، لنبدأ 🌺',
    icon: '🎉',
    action: 'silent',
  },
};

export { DEFAULT_SETTINGS };

export async function getNotificationSettings(): Promise<NotificationSettings> {
  const snap = await getDoc(SETTINGS_DOC);
  if (!snap.exists()) return DEFAULT_SETTINGS;
  const data = snap.data();
  const tamkeen: Record<string, NotificationRule> = {};
  for (const key of Object.keys(DEFAULT_SETTINGS.tamkeen)) {
    tamkeen[key] = { ...DEFAULT_SETTINGS.tamkeen[key], ...data.tamkeen?.[key] };
  }
  return {
    tamkeen,
    welcome: { ...DEFAULT_SETTINGS.welcome, ...data.welcome },
  };
}

export async function saveNotificationSettings(settings: Partial<NotificationSettings>): Promise<void> {
  await setDoc(SETTINGS_DOC, settings, { merge: true });
}

export async function getCustomNotifications(): Promise<CustomNotification[]> {
  const q = query(CUSTOM_COL, orderBy('createdAt', 'desc'));
  const snap = await getDocs(q);
  return snap.docs.map(d => ({ id: d.id, ...d.data() }) as CustomNotification);
}

export async function getActiveCustomNotifications(): Promise<CustomNotification[]> {
  const q = query(CUSTOM_COL, where('active', '==', true), orderBy('createdAt', 'desc'), limit(50));
  const snap = await getDocs(q);
  return snap.docs.map(d => ({ id: d.id, ...d.data() }) as CustomNotification);
}

export function subscribeActiveCustomNotifications(callback: (items: CustomNotification[]) => void): Unsubscribe {
  const q = query(CUSTOM_COL, where('active', '==', true), orderBy('createdAt', 'desc'), limit(50));
  return onSnapshot(q, (snap) => {
    callback(snap.docs.map(d => ({ id: d.id, ...d.data() }) as CustomNotification));
  }, (err) => {
    console.error('Custom notifications listener error:', err);
    callback([]);
  });
}

export async function addCustomNotification(data: Record<string, unknown>): Promise<unknown> {
  return addDoc(CUSTOM_COL, { ...data, active: true, createdAt: serverTimestamp() });
}

export async function updateCustomNotification(id: string, data: Record<string, unknown>): Promise<void> {
  await updateDoc(doc(db, 'custom_notifications', id), data);
}

export async function deleteCustomNotification(id: string): Promise<void> {
  await deleteDoc(doc(db, 'custom_notifications', id));
}
